import { useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useProjectChat } from "@services/ProjectChatContext";
import { useSidebar } from "@services/SidebarContext";
import { ProjectOverview } from "@shared/interfaces/ProjectsFiles";
import { hasPermission, Permission } from "@utils/permissions";
import { useProject } from "@utils/useProject";
import { useUserService } from "@utils/useUsers";
import { Member, User } from "@shared/interfaces/User";

export const useProjectData = () => {
  const { projectId, sharedToken } = useParams();
  const { setProject } = useProjectChat();
  const { refreshProjects } = useSidebar();
  const navigate = useNavigate();

  const { deleteProject, getProjectById, getSharedProject } = useProject();
  const { getCurrentUser } = useUserService();

  const [user, setUser] = useState<User>();
  const [selectedProject, setSelectedProject] =
    useState<ProjectOverview | null>(null);
  const [owner, setOwner] = useState({ ownerFirstName: "", ownerLastName: "" });
  const [permissions, setPermissions] = useState<string[]>([]);
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isFetchingProject, setIsFetchingProject] = useState(true);
  const [canViewProjectFiles, setCanViewProjectFiles] = useState(false);
  const [canViewProject, setCanViewProject] = useState(false);
  const [canViewReportFiles, setCanViewReportFiles] = useState(false);

  const fetchUserAndProject = useCallback(
    async (
      setUsers: (members: Member[]) => void,
      fetchFiles: () => void,
      fetchMembers: () => void,
      setFiles: (files: any[]) => void,
      activeTab: string
    ) => {
      try {
        const user = await getCurrentUser();
        if (!user) {
          console.error("Failed to load user!");
          return;
        }

        setUser(user);
        setLoading(true);
        if (!projectId) return;

        const project = await getProjectById(projectId);
        if (project) {
          setSelectedProject(project);
          setProject(projectId || "", project.name);
          setOwner({
            ownerFirstName: project.creator.first_name,
            ownerLastName: project.creator.last_name,
          });

          if (project.creator.id === user.id) {
            setIsOwner(true);
            if (activeTab === "Overview") {
              fetchMembers();
            }
            setCanViewProjectFiles(true);
          } else {
            setPermissions(project.permissions);
            fetchMembers();
            setCanViewProjectFiles(
              hasPermission(project.permissions, Permission.CAN_VIEW_FILE)
            );
            setCanViewProject(
              hasPermission(project.permissions, Permission.CAN_VIEW)
            );
            setCanViewReportFiles(
              hasPermission(
                project.permissions,
                Permission.CAN_VIEW_REPORTS_FILES
              )
            );
          }
        }
      } catch (error) {
        try {
          if (sharedToken) {
            const project = await getSharedProject(sharedToken);
            if (project) {
              setSelectedProject(project);
              setProject(projectId || "", project.name);
              setPermissions(project.permissions);
            }
          }
        } catch (error) {
          console.error("Error fetching project:", error);
        }
      } finally {
        setLoading(false);
      }
    },
    [projectId, sharedToken]
  );

  const fetchUpdatedProject = useCallback(async () => {
    if (!projectId) return;

    try {
      setIsFetchingProject(true);
      const data = await getProjectById(projectId);
      setSelectedProject(data);
    } catch (error) {
      console.error("Error fetching updated project:", error);
    } finally {
      setIsFetchingProject(false);
    }
  }, [projectId]);

  const handleDeleteClick = useCallback(async () => {
    if (!selectedProject) return;

    try {
      await deleteProject(selectedProject.id);
      refreshProjects();
      navigate("/assistant");
    } catch (error) {
      console.error("Error deleting project:", error);
    }
  }, [selectedProject]);

  return {
    user,
    setSelectedProject,
    selectedProject,
    owner,
    isOwner,
    permissions,
    loading,
    isFetchingProject,
    canViewProjectFiles,
    canViewProject,
    canViewReportFiles,
    setPermissions,
    fetchUserAndProject,
    fetchUpdatedProject,
    handleDeleteClick,
  };
};
